"use client";
import { fadeUpAnimationData } from "@/data/animations.data";
import { NavLink, SubNavItem } from "@/data/navbar.data";
import { AnimatePresence, motion } from "framer-motion";
import { usePathname } from "next/navigation";
import React from "react";
import { TransitionLink } from "../shared/TransitionLink";
import { useDict } from "@/components/providers/LocaleProvider";

function cx(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

const DesktopSubMenu = ({
  entry,
  open,
  setOpen,
  startCloseTimer,
  cancelCloseTimer,
  style,
  buttonProps,
}: {
  entry: NavLink;
  open: string | null;
  setOpen: React.Dispatch<React.SetStateAction<string | null>>;
  startCloseTimer: () => void;
  cancelCloseTimer: () => void;
  style?: React.CSSProperties;
  buttonProps?: React.HTMLAttributes<HTMLButtonElement>;
}) => {
  const pathname = usePathname();
  const t = useDict();
  const isGroupActive = (items: SubNavItem[]) =>
    items?.some?.((it) => pathname?.startsWith(it.href));

  const { id: group, subItems: items = [] } = entry;
  const groupLabel = t.nav.links[entry.id];
  const active = isGroupActive(items || []);
  const isOpen = open === group;


  return (
    <div
      key={group}
      className="relative"
      onMouseEnter={() => {
        cancelCloseTimer();
        setOpen(group);
      }}
      onMouseLeave={startCloseTimer}
    >
      <button
        style={style}
        className={cx(
          "py-2 lg:py-1.5 xl:py-2 flex flex-col justify-center items-center cursor-pointer hover:text-black gap-[2px] rounded-full transition-colors duration-300 z-10",
          active ? " bg-white text-black" : "",
          isOpen && !active ? " shadow-sm text-white" : "",

        )}
        aria-expanded={isOpen}
        {...buttonProps}
      >
        {<entry.Icon className="lg:hidden" size={entry.iconSize} />}
        <span className="text-[10px] lg:text-base">{groupLabel}</span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            {...fadeUpAnimationData}
            onMouseEnter={cancelCloseTimer}
            onMouseLeave={startCloseTimer}
            className={`absolute left-1/2   -translate-x-1/2 duration-300 `}
          >
            <div className="border shadow-xl rounded-2xl  border-gray-800  bg-dark-gray ring-1 ring-black/5 mt-2 w-[500px]">
              <ul className="grid grid-cols-2 gap-2 p-3">
                {items.map((item) => {
                  const Icon = item.icon;
                  const subActive = pathname === item.href;
                  const sub = t.nav.subItems[item.id];
                  return (
                    <li key={item.href}>
                      <TransitionLink
                        href={item.href}
                        className={cx(
                          "group flex items-start gap-3 rounded-xl p-3 transition-all",
                          "hover:bg-background hover:text-white",
                          subActive
                            ? "ring-1 ring-primary/40: bg-background text-white"
                            : ""
                        )}
                      >
                        <span
                          className={cx(
                            "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
                            " bg-background",
                            "group-hover:shadow-sm group-hover:border-dark-gray/40"
                          )}
                        >
                          <Icon className="w-5 h-5" aria-hidden="true" />
                        </span>
                        <span className="min-w-0">
                          <span
                            className={cx(
                              "block text-sm font-semibold leading-5",
                              subActive
                                ? "text-white"
                                : "text-white group-hover:text-white"
                            )}
                          >
                            {sub.label}
                          </span>
                          <span className="block text-xs truncate text-gray-300 group-hover:text-white/70">
                            {sub.desc}
                          </span>
                        </span>
                        <span className="w-2 h-2 mt-1 ml-auto transition-opacity rounded-full opacity-0 min-w-2 min-h-2 max-w-2 max-h-2 bg-primary group-hover:opacity-100" />
                      </TransitionLink>
                    </li>
                  );
                })}
              </ul>

              <div className="flex items-center justify-end px-4 py-2 border-t border-zinc-200/60">
                <span className="text-[11px] text-white">{groupLabel}</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DesktopSubMenu;
